import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fileTypeFromBlob } from 'file-type';
import { forkJoin, from, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StatesMasterList } from '../models/geo.model';
import {
    AssessmentRankingApiItem,
    ChallengeFundEvaluationFilterParams,
    ChallengeFundEvaluationPayload,
    ChallengeFundEvaluationRecord,
    ChallengeFundPayload,
    ChallengeFundResponse,
    CompletedEnterpriseOption,
    EnterpriseFilterContent,
    EnterpriseRankingResponse,
    FundTrackingDisbursementPayload,
    FundTrackingResponse,
    IncubatorApplicationConsentPayload,
    IncubatorApplicationCreatePayload,
    IncubatorApplicationWorkflowTransitionPayload,
    IncubatorBasicDetailsBase,
    IncubatorEvaluationSchedule,
    IncubatorHrDetailsCreatePayload,
    IncubatorHrDetailsRecord,
    IncubatorHrDetailsUpdatePayload,
    IncubatorShortlistingApplication,
    IncubatorTotalOutlayPayload,
    IncubatorUserMapping,
    IncubatorWorkflowCountResponse,
    MemberTransactionMeetings,
    ModuleAssessmentOption,
    ModuleAssessmentResult,
    ModuleBase,
    ModuleDetailsApiResponse,
    PerformanceAssessmentPayload
} from '../models/incubator.model';
import { LokosResponse, Response, TableResponse } from '../models/response.model';

const programBaseUrl = '/api/incubator-program/v1/incubator-program';
const challengeBaseUrl = '/api/incubator-challenge/v1/incubator-challenge';
const incubatorEvaluationBaseUrl = '/api/ince-evaluation/v1/ince-evaluation';
const incubatorVaultBaseUrl = '/api/vault/incubator/v1';
const incubatorVaultAccount = 'incubator-vault';
const transactionBaseUrl = `${environment.fdmTransactionsUrl}v3`;

@Injectable({
    providedIn: 'root'
})
export class IncubatorService {
    constructor(private http: HttpClient) {}

    getMemberTransactionMeetings(stateId: number, shgId: number, cboUuid: string, memId: number) {
        return this.http.get<StatesMasterList[]>('https://cdn.lokos.in/lokos-masterdata/statemaster.json').pipe(
            switchMap((stateMaster) =>
                this.http.get<LokosResponse<MemberTransactionMeetings>>(
                    `${transactionBaseUrl}/lokos-transaction/meeting/shg/member-transaction-meetings`,
                    {
                        params: { shgId, cboUuid, memId },
                        headers: {
                            'x-tenant-identifier': `dynamic_lokos_${stateMaster.find((state) => state.state_id === stateId)!.state_short_name_en.toLowerCase()}`
                        }
                    }
                )
            )
        );
    }

    getDocument(incubatorId: string, fileName: string): Observable<Blob> {
        return this.http.get(
            `${incubatorVaultBaseUrl}/${encodeURIComponent(incubatorId)}/${encodeURIComponent(fileName)}`,
            { responseType: 'blob' }
        );
    }

    uploadDocument(incubatorId: string, file: File) {
        const fileName = file.name.replaceAll(' ', '_');
        return from(fileTypeFromBlob(file)).pipe(
            switchMap((type) =>
                this.http.put<void>(`${incubatorVaultBaseUrl}/${incubatorId}/${fileName}`, file, {
                    headers: {
                        'Content-Type': type?.mime || 'application/octet-stream'
                    }
                })
            )
        );
    }

    getUserIncubatorMapping(userSubId: string) {
        const url = `${programBaseUrl}/incubatorsByUserId/${userSubId}`;
        return this.http.get<Response<IncubatorUserMapping[]>>(url);
    }

    getBasicDetails(params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<IncubatorBasicDetailsBase[]>>(`${programBaseUrl}/incubators`, {
            params
        });
    }

    getBasicDetailsById(incubator_id: string) {
        return this.http.get<Response<IncubatorBasicDetailsBase>>(`${programBaseUrl}/incubators/${incubator_id}`);
    }

    createBasicDetails(payload: IncubatorBasicDetailsBase) {
        return this.http.post<Response<IncubatorBasicDetailsBase>>(`${programBaseUrl}/incubators`, payload);
    }

    updateBasicDetails(incubator_id: string, payload: IncubatorBasicDetailsBase) {
        return this.http.patch<Response<IncubatorBasicDetailsBase>>(
            `${programBaseUrl}/incubators/${incubator_id}`,
            payload
        );
    }

    createTotalOutlay(incubatorId: string, payload: IncubatorTotalOutlayPayload) {
        return this.http.post<Response<IncubatorTotalOutlayPayload>>(
            `${programBaseUrl}/${incubatorId}/total-outlay`,
            payload
        );
    }

    updateTotalOutlay(incubatorId: string, payload: IncubatorTotalOutlayPayload) {
        return this.http.patch<Response<IncubatorTotalOutlayPayload>>(
            `${programBaseUrl}/${incubatorId}/total-outlay`,
            payload
        );
    }

    getTotalOutlay(incubatorId: string) {
        return this.http.get<Response<IncubatorTotalOutlayPayload>>(`${programBaseUrl}/${incubatorId}/total-outlay`);
    }

    createApplication(payload: IncubatorApplicationCreatePayload) {
        return this.http.post<Response<{ id?: string }>>(`${programBaseUrl}/applications`, payload);
    }

    submitApplicationConsent(applicationId: string, payload: IncubatorApplicationConsentPayload) {
        return this.http.patch<Response<{ id?: string }>>(
            `${programBaseUrl}/applications/${applicationId}/consent`,
            payload
        );
    }

    getApplications(incubatorId: string, params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<IncubatorShortlistingApplication[]>>(
            `${programBaseUrl}/incubators/${incubatorId}/applications`,
            { params }
        );
    }

    getWorkflowCount(incubatorId: string) {
        return this.http.get<Response<IncubatorWorkflowCountResponse> | IncubatorWorkflowCountResponse>(
            `${programBaseUrl}/incubators/${incubatorId}/workflow-count`
        );
    }

    updateApplicationWorkflow(payload: IncubatorApplicationWorkflowTransitionPayload) {
        return this.http.post<Response<null>>(`${programBaseUrl}/applications/workflow-transitions`, payload);
    }

    getSchedule(incubatorId: string) {
        return this.http.get<Response<IncubatorEvaluationSchedule>>(
            `${incubatorEvaluationBaseUrl}/${incubatorId}/schedules`
        );
    }

    saveSchedule(incubatorId: string, payload: IncubatorEvaluationSchedule) {
        return this.http.post<Response<void>>(`${incubatorEvaluationBaseUrl}/${incubatorId}/schedules`, payload);
    }

    updateSchedule(incubatorId: string, payload: IncubatorEvaluationSchedule) {
        return this.http.patch<Response<void>>(`${incubatorEvaluationBaseUrl}/${incubatorId}/schedules`, payload);
    }

    createModule(incubatorId: string, payload: ModuleBase) {
        return this.http.post<Response<void>>(`${incubatorEvaluationBaseUrl}/${incubatorId}/modules`, payload);
    }

    updateModule(incubatorId: string, moduleId: string, payload: ModuleBase) {
        return this.http.patch<Response<void>>(
            `${incubatorEvaluationBaseUrl}/${incubatorId}/modules/${moduleId}`,
            payload
        );
    }

    getModules(incubatorId: string) {
        return this.http.get<Response<ModuleBase[]> | TableResponse<ModuleBase[]>>(
            `${incubatorEvaluationBaseUrl}/${incubatorId}/modules`
        );
    }

    getModuleDetails(incubatorId: string, moduleId: string) {
        return this.http.get<Response<ModuleDetailsApiResponse>>(`${incubatorEvaluationBaseUrl}/modules/${moduleId}`, {
            params: { incubatorId }
        });
    }

    getModulesForAssessment(incubatorId: string) {
        return this.http.get<Response<ModuleAssessmentOption[]>>(`${incubatorEvaluationBaseUrl}/modules`, {
            params: { incubatorId }
        });
    }

    getCompletedEnterprises(incubatorId: string) {
        return this.http.get<Response<CompletedEnterpriseOption[]>>(
            `${incubatorEvaluationBaseUrl}/enterprises/completed`,
            {
                params: { incubatorId }
            }
        );
    }

    getModuleAssessmentResult(moduleId: string, marks: number) {
        return this.http.get<Response<ModuleAssessmentResult>>(
            `${incubatorEvaluationBaseUrl}/modules/${moduleId}/result`,
            { params: { marks } }
        );
    }

    createPerformanceAssessment(payload: PerformanceAssessmentPayload) {
        return this.http.post<Response<void>>(`${incubatorEvaluationBaseUrl}/assessments`, payload);
    }

    updatePerformanceAssessment(assessmentId: string, payload: PerformanceAssessmentPayload) {
        return this.http.patch<Response<void>>(`${incubatorEvaluationBaseUrl}/assessments/${assessmentId}`, payload);
    }

    getPerformanceAssessments(params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<PerformanceAssessmentPayload[]>>(
            `${incubatorEvaluationBaseUrl}/assessments/filter`,
            { params }
        );
    }

    getAssessmentRankings(params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<AssessmentRankingApiItem[]>>(
            `${incubatorEvaluationBaseUrl}/assessments/rankings`,
            { params }
        );
    }

    deleteModule(incubatorId: string, moduleId: string) {
        return this.http.delete<Response<void>>(`${incubatorEvaluationBaseUrl}/${incubatorId}/modules/${moduleId}`);
    }

    deletePerformanceAssessment(assessmentId: string) {
        return this.http.delete<Response<void>>(`${incubatorEvaluationBaseUrl}/assessments/${assessmentId}`);
    }

    getFiles(filenames: string[]): Observable<Blob[]> {
        return forkJoin(
            filenames.map((fileName) =>
                this.http.get(`${incubatorVaultBaseUrl}/${incubatorVaultAccount}/${fileName}`, {
                    responseType: 'blob'
                })
            )
        );
    }

    uploadFile(file: File): Observable<void> {
        return from(fileTypeFromBlob(file)).pipe(
            switchMap((type) =>
                this.http.put<void>(
                    `${incubatorVaultBaseUrl}/${incubatorVaultAccount}/${file.name.replaceAll(' ', '_')}`,
                    file,
                    {
                        headers: {
                            'Content-Type': type?.mime || 'application/octet-stream',
                            'x-ms-blob-type': 'BlockBlob'
                        }
                    }
                )
            )
        );
    }

    deleteBasicDetails(incubator_id: string) {
        return this.http.delete<Response<null>>(`${programBaseUrl}/incubators/${incubator_id}`);
    }

    createHrDetails(incubator_id: string, payload: IncubatorHrDetailsCreatePayload) {
        return this.http.post<Response<IncubatorHrDetailsRecord>>(
            `${programBaseUrl}/incubators/${incubator_id}/hr-details`,
            payload
        );
    }

    getHrDetails(incubatorId: string, params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<IncubatorHrDetailsRecord[]>>(
            `${programBaseUrl}/incubators/${incubatorId}/hr-details`,
            {
                params
            }
        );
    }

    getHrDetailsById(incubatorId: string, hrDetailsId: string) {
        return this.http.get<Response<IncubatorHrDetailsRecord>>(
            `${programBaseUrl}/incubators/${incubatorId}/hr-details/${hrDetailsId}`
        );
    }

    updateHrDetails(incubatorId: string, hrDetailsId: string, payload: IncubatorHrDetailsUpdatePayload) {
        return this.http.patch<Response<IncubatorHrDetailsRecord>>(
            `${programBaseUrl}/incubators/${incubatorId}/hr-details/${hrDetailsId}`,
            payload
        );
    }

    deleteHrDetails(incubatorId: string, hrDetailsId: string) {
        return this.http.delete<Response<null>>(
            `${programBaseUrl}/incubators/${incubatorId}/hr-details/${hrDetailsId}`
        );
    }

    // === Challenge Fund ===
    createChallengeFundDetails(payload: ChallengeFundPayload) {
        return this.http.post<Response<ChallengeFundResponse>>(`${challengeBaseUrl}/`, payload);
    }

    getFundDetails(params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<ChallengeFundResponse[]>>(`${challengeBaseUrl}/filter`, {
            params
        });
    }

    getFundDetailsById(incubatorId: string) {
        return this.http.get<Response<ChallengeFundResponse>>(`${challengeBaseUrl}/${incubatorId}`);
    }

    updateChallengeFundDetails(incubator_id: string, payload: ChallengeFundPayload) {
        return this.http.patch<Response<ChallengeFundResponse>>(`${challengeBaseUrl}/${incubator_id}`, payload);
    }

    deleteChallengeFundDetails(incubator_id: string) {
        return this.http.delete<Response<null>>(`${challengeBaseUrl}/${incubator_id}`);
    }

    // ---------- Challenege Fund Evealuation-----
    createChallengeFundEvaluation(payload: ChallengeFundEvaluationPayload) {
        return this.http.post<Response<ChallengeFundEvaluationRecord>>(`${challengeBaseUrl}/evaluation/`, payload);
    }

    getChallengeFundEvaluations(params: ChallengeFundEvaluationFilterParams) {
        return this.http.get<TableResponse<ChallengeFundEvaluationRecord[]>>(`${challengeBaseUrl}/evaluation/filter`, {
            params: params as unknown as Record<string, string | number | boolean>
        });
    }

    deleteChallengeFundEvaluation(evalutionId: string) {
        return this.http.delete<Response<null>>(`${challengeBaseUrl}/evaluation/${evalutionId}`);
    }

    getEnterpriseDetails(params: Record<string, string | number | boolean>) {
        return this.http.get<TableResponse<EnterpriseFilterContent[]>>(
            `${incubatorEvaluationBaseUrl}/assessments/rankings/completed`,
            {
                params
            }
        );
    }
    // ---------------------Enterprise Ranking------

    getEnterpriseRanking(
        incubatorId: string,
        params: {
            page?: number;
            size?: number;
            sort_by?: string;
            sort_order?: 'ASC' | 'DESC';
            keyword?: string;
        }
    ) {
        return this.http.get<TableResponse<EnterpriseRankingResponse[]>>(
            `${challengeBaseUrl}/${incubatorId}/rankings`,
            { params }
        );
    }

    getChallengeFundTracking(
        incubatorId: string,
        params: {
            page?: number;
            size?: number;
            sort_by?: string;
            sort_order?: 'ASC' | 'DESC';
            keyword?: string;
            enterprise_category?: string;
            enterprise_sector?: string;
        }
    ) {
        return this.http.get<TableResponse<FundTrackingResponse[]>>(
            `${challengeBaseUrl}/${incubatorId}/fund-tracking`,
            { params }
        );
    }

    createFundDisbursementDetails(payload: FundTrackingDisbursementPayload) {
        return this.http.post<Response<FundTrackingDisbursementPayload>>(`${challengeBaseUrl}/disbursement/`, payload);
    }

    editFundDisbursementDetails(payload: FundTrackingDisbursementPayload, fundId: string) {
        return this.http.put<Response<FundTrackingDisbursementPayload>>(
            `${challengeBaseUrl}/disbursement/${fundId}`,
            payload
        );
    }

    deleteFundDisbursementDetails(fundId: string) {
        return this.http.delete<Response<null>>(`${challengeBaseUrl}/disbursement/${fundId}`);
    }
}
